/**
 * Booking status values
 */
export const BOOKING_STATUS = {
  PENDING: 'pending',
  PENDING_CONFIRMATION: 'pending_confirmation',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  FAILED: 'failed',
  NO_SHOW: 'no_show',
} as const;

/**
 * Canonical registry of machine-readable booking error codes. Booking services
 * (server) return one of these stable strings; each client surface maps it to a
 * localized message (services never localize). This is the single home for
 * booking error codes — add new codes HERE instead of scattering ad-hoc string
 * sentinels, and type against the `BookingErrorCode` union below for
 * compile-time safety at every producer/consumer across the Server Action
 * boundary.
 *
 * A plain `as const` object (not a TS `enum`) is deliberate: the values are
 * plain strings that serialize cleanly across Server Actions and tree-shake,
 * while the derived union type gives the same exhaustiveness a string enum would
 * without enum's nominal-typing and runtime quirks.
 */
export const BookingErrorCode = {
  /** Non-owner booking refused for falling outside a school court's designated
   *  hours. See packages/bookings/src/services/court-hours.guard.ts. */
  OutsideOperatingHours: 'OUTSIDE_OPERATING_HOURS',
} as const;

export type BookingErrorCode =
  (typeof BookingErrorCode)[keyof typeof BookingErrorCode];

/**
 * Payment status values
 */
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PARTNER_PENDING: 'partner_pending',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  PACKAGE_CREDIT: 'package_credit',
  CASH_UNPAID: 'cash_unpaid',
  CASH_PAID: 'cash_paid',
  ONLINE_PAID: 'online_paid',
  UNPAID: 'unpaid',
  SPLIT_PENDING: 'split_pending',
  CREDITED: 'credited',
} as const;

/**
 * Package payment states whose credits may be redeemed.
 *
 * Derived by recalculate_package_payment_status (20-booking-packages.sql):
 * unpaid (nothing paid) → split_pending (part-paid) → succeeded/wallet_paid
 * (fully paid), plus refunded and the package_credit carry-over.
 *
 * Collection status does NOT gate redemption. Staff routinely hand a customer
 * a package before the money lands — assigned manually, comped, sold on
 * credit, or paid in installments ("half now, half later"). Withholding those
 * credits strands them silently: the customer's own package list still shows
 * "Active, N left" while every booking surface offers nothing.
 *
 * refunded is the only withheld state — the money went back, so the credits
 * are void.
 *
 * NEVER add split_paid. recalculate_package_payment_status is the sole writer
 * of a package's payment_status and can only emit unpaid | split_pending |
 * succeeded | wallet_paid | refunded; split_paid is a BOOKING status and no
 * package row ever carries it.
 *
 * History: this allow-list was added by PR #111 (3e4c76b9b, Aug 25 2026) as a
 * pure addition — no payment_status filter existed before it, so unpaid and
 * split_pending redeemed freely. Both were excluded by omission rather than
 * decision, and both stranded live customers once it reached prod in PR #200.
 */
export const REDEEMABLE_PACKAGE_PAYMENT_STATUSES = [
  'succeeded',
  'cash_paid',
  'card_paid',
  'online_paid',
  'bank_transfer_paid',
  'wallet_paid',
  'package_credit',
  'split_pending',
  'unpaid',
] as const;

/**
 * Class-seat payment statuses the customer may settle from their wallet.
 * Must stay aligned with settle_wallet_class_participant in 59-wallet.sql.
 */
export const CLASS_SEAT_WALLET_SETTLEABLE_STATUSES = [
  'pending',
  'unpaid',
  'cash_unpaid',
] as const;

/** Class-seat payment statuses a package credit may settle. */
export const CLASS_SEAT_PACKAGE_PAYABLE_STATUSES = [
  'pending',
  'unpaid',
  'failed',
  'cash_unpaid',
] as const;

/**
 * Booking types
 */
export const BOOKING_TYPES = {
  REGULAR: 'regular',
  TRAINER: 'trainer',
  PACKAGE: 'package',
  BALL_MACHINE: 'ball_machine',
} as const;

/**
 * Booking sources - where the booking originated
 */
export const BOOKING_SOURCES = {
  WEB: 'web',
  WHATSAPP: 'whatsapp',
  ADMIN: 'admin',
  EXTERNAL_API: 'external_api',
} as const;

/**
 * Payment providers
 */
export const PAYMENT_PROVIDERS = {
  TAP: 'tap',
  HYPERPAY: 'hyperpay',
  STRIPE: 'stripe',
  MOYASAR: 'moyasar',
  ZIINA: 'ziina',
} as const;

/**
 * Payment methods
 */
export const PAYMENT_METHODS = {
  CASH: 'cash',
  CARD: 'card',
  PACKAGE: 'package',
  BANK_TRANSFER: 'bank_transfer',
} as const;

/**
 * Rate limiting constants
 */
export const RATE_LIMITING = {
  MAX_PENDING_PAYMENTS: 3,
  PENDING_PAYMENT_TIMEOUT_HOURS: 24,
} as const;

/**
 * Booking expiration time in minutes
 */
export const BOOKING_EXPIRATION_MINUTES = 15;

/**
 * Canonical package types — the single source of truth for the
 * `booking_packages.package_type` value, its UI label key (in the `bookings`
 * i18n namespace, so per-business-type overrides apply), and whether a booking
 * of that type is class-based (vs. court/trainer infrastructure).
 *
 * `labelKey` lives in the `bookings` namespace so studios see "Studio Only" /
 * "Private" / "Group Classes" / "Semi Private" while padel courts see "Court" /
 * "Trainer" etc. Consumers that need to hide a type for a business type should
 * filter on `t(labelKey).trim().length > 0` (a blank override hides the option).
 *
 * `isClasses` drives class-vs-infrastructure branching (label phrasing in
 * format-package-label, the class-friendly credit allow-list, etc.).
 *
 * NOTE: `semi_private` and `group_classes` are DISTINCT types. A studio can
 * offer both at once. They are NOT interchangeable — credits only redeem
 * against a matching type, so the package_type and the class category must
 * agree (see kit.booking_resource_kind + the class credit allow-list).
 */
export const PACKAGE_TYPES = [
  { value: 'court', labelKey: 'bookings:courtOnly', isClasses: false },
  { value: 'trainer', labelKey: 'bookings:coaching', isClasses: false },
  { value: 'group_classes', labelKey: 'bookings:groupClasses', isClasses: true },
  { value: 'semi_private', labelKey: 'bookings:semiPrivate', isClasses: true },
  { value: 'kids_academy', labelKey: 'bookings:kidsAcademy', isClasses: true },
] as const;

/** Union of valid package_type values, derived from PACKAGE_TYPES. */
export type PackageTypeValue = (typeof PACKAGE_TYPES)[number]['value'];

/** All package_type values as a plain string[] (for Zod .enum, .in() filters). */
export const PACKAGE_TYPE_VALUES = PACKAGE_TYPES.map((t) => t.value) as [
  PackageTypeValue,
  ...PackageTypeValue[],
];

/** value → i18n label key map, for components that look up by value. */
export const PACKAGE_TYPE_LABEL_KEYS: Record<string, string> =
  Object.fromEntries(PACKAGE_TYPES.map((t) => [t.value, t.labelKey]));

/**
 * Class-based package types — those whose credits can pay for class enrollments
 * and whose labels use "Class" phrasing. The class credit allow-list and
 * format-package-label both derive from this so adding a class-type only
 * requires editing PACKAGE_TYPES above.
 *
 * Typed as `string[]` (not the literal tuple) because it's used for runtime
 * membership checks (`.includes(someString)`) and PostgREST `.in()` filters,
 * never for type narrowing — a literal-typed array would reject `string` args.
 */
export const CLASS_PACKAGE_TYPE_VALUES: string[] = PACKAGE_TYPES.filter(
  (t) => t.isClasses,
).map((t) => t.value);

/**
 * How far ahead classes are listed for booking: /book's class calendar and
 * the WhatsApp assistant's list_classes both read this, so a session a
 * customer can see on /book is one the assistant can find. The assistant
 * looked only 5 days ahead and told a Hub71 member there was no session the
 * week the customer could see one (Active Padel, 2026-10-28).
 */
export const CLASS_AVAILABILITY_WINDOW_DAYS = 60;
